import { Link } from '@mui/joy';
import { Stack, StackProps, Typography } from '@mui/joy';
import { SxProps } from '@mui/joy/styles/types';
import { isString } from 'lodash';
import { Link as RouterLink } from 'react-router-dom';

import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { theme } from 'theme';
import { Color, Direction, Level } from 'theme/enums';

import { TextEllipsis } from '.';

interface IProps {
  label: string;
  value?: JSX.Element | number | string;
  options?: {
    hasColon?: boolean;
    inline?: boolean;
    labelColor?: Color;
    labelLevel?: Level;
    labelSx?: SxProps;
    valueColor?: Color;
    valueEllipsis?: boolean;
    valueLevel?: Level;
    valueLink?: string;
    valueSx?: SxProps;
    wrapperProps?: StackProps;
  };
}

const LabeledValue = ({ label, value, options = {} }: IProps) => {
  const {
    hasColon = true,
    labelColor = Color.NEUTRAL,
    valueColor = null,
    labelLevel = Level.BODY_XS,
    valueLevel = Level.TITLE_SM,
    valueEllipsis = false,
    valueLink = '',
    labelSx = {},
    valueSx = {},
    wrapperProps = {},
    inline = false,
  } = options;

  const getValueContent = () => {
    if (value && !isString(value)) {
      return value;
    }

    if (valueEllipsis) {
      return (
        <TextEllipsis
          content={(value as string) || DEFAULT_PLACEHOLDER}
          level={valueLevel}
          color={valueColor}
          sx={valueSx}
        />
      );
    }

    if (valueLink && value) {
      return (
        <Link to={valueLink} component={RouterLink} fontSize={14}>
          {value}
        </Link>
      );
    }

    return (
      <Typography
        level={valueLevel}
        color={valueColor}
        sx={{
          textWrap: 'wrap',
          ...valueSx,
        }}
      >
        {value || DEFAULT_PLACEHOLDER}
      </Typography>
    );
  };

  return (
    <Stack
      spacing={inline ? 0.5 : 0}
      direction={inline ? Direction.ROW : Direction.COLUMN}
      sx={{
        ...(inline && { alignItems: 'flex-end' }),
        ...wrapperProps?.sx,
      }}
      {...wrapperProps}
    >
      <Typography
        level={labelLevel}
        color={labelColor}
        sx={{
          color:
            Color.NEUTRAL === labelColor
              ? `rgba(${theme.palette.neutral.mainChannel} / 1) !important`
              : 'inherit',
          fontWeight: 500,
          ...labelSx,
        }}
      >
        {`${label}${hasColon ? ':' : ''}`}
      </Typography>

      {getValueContent()}
    </Stack>
  );
};

export default LabeledValue;
